import { getFunctions, httpsCallable } from 'firebase/functions';
import { getToken as getAppCheckToken } from 'firebase/app-check';
import { collection, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import app, { appCheck, db, functionsSa, isDev } from '../firebase';

const functions = getFunctions(app);

// Tipos para as respostas das funções
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  /** Categoria controlada retornada pelos callables administrativos. */
  code?: string;
  errorCode?: string;
  category?: string;
  errorCategory?: string;
  /** Nome de exibição permitido para contexto, nunca identificador interno. */
  relatedEntityName?: string;
  conflictingWebhookConfigId?: string;
}

// Tipos para Companies
export interface Company {
  id: string;
  name: string;
  cnpj: string;
  isActive: boolean;
  /** @deprecated Campo legado — será substituído por `lists` após migração completa (Fase 5). */
  listId?: string;
  /**
   * Mapa N listas por product, ordenadas (T113 / RFC §6.5).
   * Substituirá `listId` após o cutover. Exibido como read-only até T026/Fase 3.
   * // TODO T026/Fase 3 — setCompanyLists: editor completo do mapa em CompaniesPage (T113)
   */
  lists?: { [productId: string]: string[] };
  activity?: string;
  logoUrl?: string;
  /** Id do doc em `evo_webhook_configs` quando vinculado (denormalizado no Firestore). */
  evoWebhookConfigId?: string;
  createdAt: any;
  updatedAt: any;
  networkId?: string;
  companyGroupId?: string;
  nameFold?: string;
  network?: CompanyNetworkSummary | null;
  companyGroup?: CompanyGroupSummary | null;
}

export interface CreateCompanyData {
  name: string;
  cnpj: string;
  isActive: boolean;
  listId?: string;
  /** Na criação, `null` representa campo opcional ausente. */
  activity?: string | null;
  /** Na criação, `null` representa campo opcional ausente. */
  logoUrl?: string | null;
  addToReedyFiscal?: boolean;
  networkId?: string | null;
  companyGroupId?: string | null;
}

export interface UpdateCompanyData {
  name?: string;
  isActive?: boolean;
  listId?: string;
  /** `null` remove o campo opcional existente. */
  activity?: string | null;
  /** `null` remove o campo opcional existente. */
  logoUrl?: string | null;
  addToReedyFiscal?: boolean;
  networkId?: string | null;
}

export interface CompanyNetworkSummary {
  id: string;
  name: string;
}

export interface CompanyGroupSummary {
  id: string;
  name: string;
}

export interface CompanyPageCursor {
  nameFold: string;
  id: string;
}

export interface ListCompaniesPageRequest {
  pageSize?: number;
  cursor?: CompanyPageCursor;
  search?: string;
  networkId?: string;
  companyGroupId?: string;
}

export interface CompanyPage {
  items: Company[];
  nextCursor: CompanyPageCursor | null;
  totalCount: number;
}

export interface Network {
  id: string;
  name: string;
  nameNormalized: string;
  description?: string;
  imageUrl?: string;
  imageStoragePath?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyGroup {
  id: string;
  name: string;
  nameNormalized: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

const callableData = async <T>(name: string, payload: unknown): Promise<T> => {
  const result = await httpsCallable(functionsSa, name)(payload);
  const response = result.data as ApiResponse<T>;
  if (!response.success || response.data === undefined) throw new Error(response.error || 'Não foi possível concluir a operação');
  return response.data;
};

export const networksService = {
  list: (search?: string) => callableData<{ items: Network[] }>(
    'listNetworks',
    search ? { search } : {},
  ).then((result) => result.items),
  get: (id: string) => callableData<Network>('getNetwork', { id }),
  create: (data: { name: string; description?: string }) => callableData<Network>('createNetwork', data),
  update: (id: string, data: { name?: string; description?: string | null }) => callableData<Network>(
    'updateNetwork',
    { id, ...data },
  ),
  delete: (id: string) => callableData<{
    id: string;
    unlinkedCompanies: number;
    imageCleanup: 'deleted' | 'not_applicable' | 'failed';
  }>('deleteNetwork', { id }),
  prepareImageUpload: (
    networkId: string,
    contentType: 'image/jpeg' | 'image/png' | 'image/webp',
    sizeBytes: number,
  ) => callableData<{
    uploadId: string;
    stagingPath: string;
    expiresAt: string;
  }>('prepareNetworkImageUpload', { networkId, contentType, sizeBytes }),
  finalizeImageUpload: (uploadId: string) => callableData<{ network: Network }>(
    'finalizeNetworkImageUpload',
    { uploadId },
  ),
  deleteImage: (networkId: string) => callableData<{
    network: Network;
    imageDeleted: boolean;
  }>('deleteNetworkImage', { networkId }),
};

export const companyGroupsService = {
  list: () => callableData<{ items: CompanyGroup[] }>('listCompanyGroups', {})
    .then((result) => result.items),
  get: (id: string) => callableData<CompanyGroup>('getCompanyGroup', { id }),
  create: (data: { name: string; description?: string }) => callableData<CompanyGroup>(
    'createCompanyGroup',
    data,
  ),
  update: (id: string, data: { name?: string; description?: string | null }) => callableData<CompanyGroup>(
    'updateCompanyGroup',
    { id, ...data },
  ),
  delete: (id: string) => callableData<{
    id: string;
    unlinkedCompanies: number;
  }>('deleteCompanyGroup', { id }),
};

// Tipos para Publishers (coleção Firestore `publishers`)
export interface Publisher {
  id: string;
  name: string;
  createdAt: any;
  updatedAt: any;
}

export interface CreatePublisherData {
  name: string;
}

export interface UpdatePublisherData {
  name?: string;
}

export interface PublisherGrantsReportRow {
  companyName: string;
  publisherName: string;
  titleName: string;
  quantity: number;
}

// Tipos para Titles
export interface Title {
  id: string;
  type: 'ebook' | 'audiobook';
  name: string;
  publisher: string;
  authors: string[];
  language: string;
  description: string;
  coverUrl: string;
  coverStoragePath?: string;
  file: {
    path: string;
    sizeBytes: number;
    checksum: string;
    mimeType: string;
    md5?: string;
  };
  source?: {
    storagePath: string;
    sizeBytes: number;
    md5: string;
    uploaderId?: string;
    originalName: string;
  };
  technicalInfo?: {
    durationSec: number;
    bitrate?: number;
    sampleRate?: number;
    codec?: string;
    channels?: number;
  };
  processingStatus?: {
    status: 'processing' | 'ready' | 'error';
    errors?: string[];
    processedAt?: string;
  };
  pages?: number;
  estimatedReadingTimeMin?: number;
  durationSec?: number;
  chapters?: Array<{
    title: string;
    order: number;
    startSec?: number;
    href?: string;
  }>;
  tags: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  publisherId?: string;
  productId?: string;
}

export interface DevModeCpfBookRecord {
  id: string;
  kind: 'effective' | 'pending';
  titleId: string;
  titleName: string;
  titleType: 'ebook' | 'audiobook' | '';
  companyId: string;
  companyName: string;
}

export interface DevModeGrantResult {
  titleId: string;
  titleName: string;
  status: 'created' | 'pending' | 'already_exists' | 'failed';
  message?: string;
}

export const devModeEntitlementsService = {
  getByCpf: (cpf: string) => callableData<{ items: DevModeCpfBookRecord[]; userExists: boolean }>(
    'devModeGetCpfBooks',
    { cpf },
  ),
  grant: (input: { cpf: string; companyId: string; titleIds: string[] }) => callableData<{
    results: DevModeGrantResult[];
  }>('devModeGrantCpfBooks', input),
  remove: (input: { cpf: string; records: Array<Pick<DevModeCpfBookRecord, 'id' | 'kind'>> }) => callableData<{
    deletedCount: number;
    effectiveDeleted: number;
    pendingDeleted: number;
  }>('devModeRemoveCpfBooks', input),
};

export interface CreateTitleData {
  type: 'ebook' | 'audiobook';
  name: string;
  publisher: string;
  authors: string[];
  language: string;
  description: string;
  coverUrl: string;
  file: {
    path: string;
    sizeBytes: number;
    checksum: string;
    mimeType: string;
  };
  pages?: number;
  estimatedReadingTimeMin?: number;
  durationSec?: number;
  tags: string[];
  isActive: boolean;
  publisherId?: string;
  productId?: string;
}

export interface UpdateTitleData {
  type?: 'ebook' | 'audiobook';
  name?: string;
  publisher?: string;
  authors?: string[];
  language?: string;
  description?: string;
  coverUrl?: string;
  coverStoragePath?: string;
  file?: {
    path: string;
    sizeBytes: number;
    checksum: string;
    mimeType: string;
  };
  pages?: number;
  estimatedReadingTimeMin?: number;
  durationSec?: number;
  tags?: string[];
  isActive?: boolean;
  publisherId?: string;
  productId?: string;
}

export type UpdateTitleRequest = Omit<UpdateTitleData, 'description' | 'coverUrl' | 'coverStoragePath' | 'authors' | 'tags' | 'pages' | 'publisherId' | 'productId'> & {
  description?: string | null;
  coverUrl?: string | null;
  coverStoragePath?: string | null;
  authors?: string[] | null;
  tags?: string[] | null;
  pages?: number | null;
  publisherId?: string | null;
  productId?: string | null;
};

export interface TitlePageCursor {
  name: string;
  id: string;
}

export interface ListTitlesPageRequest {
  pageSize?: number;
  cursor?: TitlePageCursor;
  search?: string;
  publisherIds?: string[];
  includeWithoutPublisher?: boolean;
}

export interface TitlePage {
  items: Title[];
  nextCursor: TitlePageCursor | null;
  totalCount: number;
}

// ---------------------------------------------------------------------------
// Módulo de Combos / Planos — T100
// Shapes espelham contracts.md "Módulo de Combos / Planos — Shapes de dados".
// ---------------------------------------------------------------------------

/**
 * Ativo entregável cadastrável (coleção `products`).
 * Informações fiscais são gerenciadas exclusivamente pelo reedy-fiscal — o Admin não lê nem grava
 * esses dados.
 */
export interface Product {
  id: string;
  tipo: 'ebook' | 'audiobook';
  titulo: string;
  nomeComercial?: string;
  isActive: boolean;
}

export interface CreateProductData {
  tipo: 'ebook' | 'audiobook';
  titulo: string;
  isActive: boolean;
}

export interface UpdateProductData {
  tipo?: 'ebook' | 'audiobook';
  titulo?: string;
  isActive?: boolean;
}

/**
 * Item de composição de um Combo.
 * Restrições (defesa em profundidade — o backend é a autoridade):
 *   qtd          ≤ 50
 *   valorUnitario ≤ 100 000
 */
export interface ComboItem {
  productId: string;
  /** Quantidade de títulos por ciclo. ≥ 1, teto 50. */
  qtd: number;
  /** Valor de referência em R$ por ciclo. Teto 100 000. */
  valorUnitario: number;
}

/**
 * Plano composto (coleção `combos`).
 * - `codigo`: identificador único e estável usado no match da venda EVO.
 *   Allowlist: ^[A-Za-z0-9_-]{2,40}$ (anti-ReDoS).
 * - `composicao`: cada `productId` deve ser único dentro do combo.
 * - `periodo`: número total de entregas, incluindo a inicial. ≥ 1, teto 36.
 */
export interface Combo {
  id: string;
  /** Allowlist: ^[A-Za-z0-9_-]{2,40}$ */
  codigo: string;
  nome: string;
  /** Total de entregas (incluindo a inicial). ≥ 1, teto 36. */
  periodo: number;
  isActive: boolean;
  /** productId deve ser único por item. */
  composicao: ComboItem[];
}

export interface CreateComboData {
  /** Allowlist: ^[A-Za-z0-9_-]{2,40}$ */
  codigo: string;
  nome: string;
  /** Total de entregas (incluindo a inicial). ≥ 1, teto 36. */
  periodo: number;
  isActive: boolean;
  composicao: ComboItem[];
}

export interface UpdateComboData {
  /** Allowlist: ^[A-Za-z0-9_-]{2,40}$ */
  codigo?: string;
  nome?: string;
  /** Total de entregas (incluindo a inicial). ≥ 1, teto 36. */
  periodo?: number;
  isActive?: boolean;
  composicao?: ComboItem[];
}

// ---------------------------------------------------------------------------
// Tipos para Lists
// ---------------------------------------------------------------------------

export interface ListItem {
  titleId: string;
  order: number;
}

export interface List {
  id: string;
  name: string;
  description?: string;
  items: ListItem[];
  isActive: boolean;
  /** Vínculo monoproduct (novo — T112). Listas legadas podem não ter este campo. */
  productId?: string;
  createdAt: any;
  updatedAt: any;
}

export interface CreateListData {
  name: string;
  /** Campo opcional vazio é enviado como null e omitido pelo backend. */
  description?: string | null;
  items: ListItem[];
  isActive: boolean;
  /** Obrigatório ao criar nova lista (T112). */
  productId?: string;
}

export interface UpdateListData {
  name?: string;
  /** `null` remove a descrição existente. */
  description?: string | null;
  items?: ListItem[];
  isActive?: boolean;
  /** Obrigatório ao salvar (T112); listas legadas sem productId devem receber este vínculo. */
  productId?: string;
}

// Tipos para Users
export interface UserCompany {
  companyId: string;
  isActive: boolean;
}

export interface User {
  id: string;
  uid: string;
  email: string;
  display_name: string;
  photo_url?: string;
  phone: string;
  cpf: string;
  profileCompleted: boolean;
  created_time: any;
  updatedAt: any;
  // Campos opcionais que podem não estar presentes
  name?: string;
  birthDate?: string;
  authProviders?: string[];
  companyIds?: UserCompany[];
  createdAt?: any;
}

// Serviços para Companies
export const companiesService = {
  listPage: (request: ListCompaniesPageRequest = {}) => callableData<CompanyPage>(
    'listCompaniesPage',
    request,
  ),
  setNetwork: (
    companyId: string,
    networkId: string | null,
    expectedNetworkId: string | null,
  ) => callableData<{
    companyId: string;
    network: CompanyNetworkSummary | null;
    updatedAt: string;
  }>('setCompanyNetwork', { companyId, networkId, expectedNetworkId }),
  setCompanyGroup: (
    companyId: string,
    companyGroupId: string | null,
    expectedCompanyGroupId: string | null,
  ) => callableData<{
    companyId: string;
    companyGroup: CompanyGroupSummary | null;
    updatedAt: string;
  }>('setCompanyGroup', { companyId, companyGroupId, expectedCompanyGroupId }),
  // Buscar todas as empresas
  async getAll(): Promise<Company[]> {
    try {
      const getAllCompaniesFunction = httpsCallable(functions, 'getAllCompanies');
      const result = await getAllCompaniesFunction({});
      const response = result.data as ApiResponse<Company[]>;

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw new Error('Failed to fetch companies');
    }
  },

  // Criar empresa
  async create(data: CreateCompanyData): Promise<ApiResponse<Company>> {
    const createCompanyFunction = httpsCallable(functions, 'createCompany');
    const result = await createCompanyFunction(data);
    return result.data as ApiResponse<Company>;
  },

  // Atualizar empresa
  async update(id: string, data: UpdateCompanyData): Promise<ApiResponse> {
    const updateCompanyFunction = httpsCallable(functions, 'updateCompany');
    const result = await updateCompanyFunction({ id, ...data });
    return result.data as ApiResponse;
  },

  // Deletar empresa
  async delete(id: string): Promise<ApiResponse> {
    const deleteCompanyFunction = httpsCallable(functions, 'deleteCompany');
    const result = await deleteCompanyFunction({ id });
    return result.data as ApiResponse;
  },

  // Ativar/desativar empresa
  async toggleActive(id: string): Promise<ApiResponse> {
    const toggleCompanyActiveFunction = httpsCallable(functions, 'toggleCompanyActive');
    const result = await toggleCompanyActiveFunction({ id });
    return result.data as ApiResponse;
  },

  // Buscar empresa por CNPJ
  async findByCnpj(cnpj: string): Promise<ApiResponse<Company>> {
    const findCompanyByCnpjFunction = httpsCallable(functions, 'findCompanyByCnpj');
    const result = await findCompanyByCnpjFunction({ cnpj });
    return result.data as ApiResponse<Company>;
  },

  // TODO: criar Cloud Function dedicada `getCompanyById` para evitar buscar todas as empresas
  async getById(id: string): Promise<Company> {
    const all = await this.getAll();
    const found = all.find((c) => c.id === id);
    if (!found) throw new Error(`Empresa ${id} não encontrada`);
    return found;
  },

  /**
   * Persiste o mapa ordenado `productId → listId[]` da empresa (T113 ⟵ `setCompanyLists`/T026).
   * Callable na instância padrão `us-central1` (sem região — vide contracts.md).
   * A ordem de cada array é significativa: é consumida pelo grant (RFC §11.f).
   * O backend preserva `company.listId` legado intacto (coexistência até Fase 5).
   */
  async setLists(
    companyId: string,
    lists: { [productId: string]: string[] }
  ): Promise<ApiResponse> {
    const setCompanyListsFunction = httpsCallable(functions, 'setCompanyLists');
    const result = await setCompanyListsFunction({ companyId, lists });
    return result.data as ApiResponse;
  },
};

// Serviços para Publishers
export const publishersService = {
  async getAll(): Promise<Publisher[]> {
    try {
      const getAllPublishersFunction = httpsCallable(functions, 'getAllPublishers');
      const result = await getAllPublishersFunction({});
      const response = result.data as ApiResponse<Publisher[]>;
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch publishers');
    } catch (error) {
      console.error('Error fetching publishers:', error);
      throw new Error('Failed to fetch publishers');
    }
  },

  async create(data: CreatePublisherData): Promise<ApiResponse<Publisher>> {
    const createPublisherFunction = httpsCallable(functions, 'createPublisher');
    const result = await createPublisherFunction(data);
    return result.data as ApiResponse<Publisher>;
  },

  async update(id: string, data: UpdatePublisherData): Promise<ApiResponse> {
    const updatePublisherFunction = httpsCallable(functions, 'updatePublisher');
    const result = await updatePublisherFunction({ id, ...data });
    return result.data as ApiResponse;
  },

  async delete(id: string): Promise<ApiResponse> {
    const deletePublisherFunction = httpsCallable(functions, 'deletePublisher');
    const result = await deletePublisherFunction({ id });
    return result.data as ApiResponse;
  },

  async getTitlesByPublisherId(publisherId: string): Promise<Title[]> {
    try {
      const getTitlesByPublisherIdFunction = httpsCallable(functions, 'getTitlesByPublisherId');
      const result = await getTitlesByPublisherIdFunction({ publisherId });
      const response = result.data as ApiResponse<Title[]>;
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch titles');
    } catch (error) {
      console.error('Error fetching titles by publisher:', error);
      throw new Error('Failed to fetch titles');
    }
  },

  /** Remove vínculo editora–título. Passe `onlyIfPublisherId` na tela da editora para garantir que o título pertence a ela. */
  async clearTitlePublisherLink(titleId: string, onlyIfPublisherId?: string): Promise<ApiResponse<{ changed: boolean }>> {
    const fn = httpsCallable(functions, 'clearTitlePublisherLink');
    const payload: { titleId: string; onlyIfPublisherId?: string } = { titleId };
    if (onlyIfPublisherId) payload.onlyIfPublisherId = onlyIfPublisherId;
    const result = await fn(payload);
    return result.data as ApiResponse<{ changed: boolean }>;
  },

  /** Substitui o conjunto de títulos vinculados à editora (desvincula os que saíram, vincula os novos). */
  async syncPublisherTitles(publisherId: string, titleIds: string[]): Promise<ApiResponse<{ linked: number; unlinked: number }>> {
    const fn = httpsCallable(functions, 'syncPublisherTitles');
    const result = await fn({ publisherId, titleIds });
    return result.data as ApiResponse<{ linked: number; unlinked: number }>;
  },

  async getGrantsReportByPeriod(params: {
    publisherId: string;
    dateStart: string;
    dateEnd: string;
  }): Promise<ApiResponse<{ publisherName: string; rows: PublisherGrantsReportRow[] }>> {
    const fn = httpsCallable(functions, 'getPublisherGrantsReportByPeriod', { timeout: 300_000 });
    const result = await fn(params);
    return result.data as ApiResponse<{ publisherName: string; rows: PublisherGrantsReportRow[] }>;
  },
};

// Serviços para Titles
export const titlesService = {
  listPage: (request: ListTitlesPageRequest = {}) => callableData<TitlePage>(
    'listTitlesPage',
    request,
  ),
  // Buscar todos os títulos
  async getAll(): Promise<Title[]> {
    try {
      const getAllTitlesFunction = httpsCallable(functions, 'getAllTitles');
      const result = await getAllTitlesFunction({});
      const response = result.data as ApiResponse<Title[]>;

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch titles');
      }
    } catch (error) {
      console.error('Error fetching titles:', error);
      throw new Error('Failed to fetch titles');
    }
  },

  // Criar título
  async create(data: CreateTitleData): Promise<ApiResponse<Title>> {
    const createTitleFunction = httpsCallable(functions, 'createTitle');
    const result = await createTitleFunction(data);
    return result.data as ApiResponse<Title>;
  },

  // Atualizar título
  async update(id: string, data: UpdateTitleRequest): Promise<ApiResponse> {
    const updateTitleFunction = httpsCallable(functions, 'updateTitle');
    const result = await updateTitleFunction({ id, ...data });
    return result.data as ApiResponse;
  },

  // Deletar título
  async delete(id: string): Promise<ApiResponse> {
    const deleteTitleFunction = httpsCallable(functions, 'deleteTitle');
    const result = await deleteTitleFunction({ id });
    return result.data as ApiResponse;
  },
};

// Serviços para Lists
export const listsService = {
  // Buscar todas as listas
  async getAll(): Promise<List[]> {
    try {
      const getAllListsFunction = httpsCallable(functions, 'getAllLists');
      const result = await getAllListsFunction({});
      const response = result.data as ApiResponse<List[]>;

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch lists');
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
      throw new Error('Failed to fetch lists');
    }
  },

  // Criar lista
  async create(data: CreateListData): Promise<ApiResponse<List>> {
    const createListFunction = httpsCallable(functions, 'createList');
    const result = await createListFunction(data);
    return result.data as ApiResponse<List>;
  },

  // Atualizar lista
  async update(id: string, data: UpdateListData): Promise<ApiResponse> {
    const updateListFunction = httpsCallable(functions, 'updateList');
    const result = await updateListFunction({ id, ...data });
    return result.data as ApiResponse;
  },

  // Deletar lista
  async delete(id: string): Promise<ApiResponse> {
    const deleteListFunction = httpsCallable(functions, 'deleteList');
    const result = await deleteListFunction({ id });
    return result.data as ApiResponse;
  },
};

// ---------------------------------------------------------------------------
// Serviços para Products — T101
// Região: southamerica-east1 (todos os callables do módulo de Combos).
// Resposta padrão { success: true, data: T } — mesmo padrão dos demais serviços.
// Erros do gate (admin/permissão/App Check) propagam a mensagem legível do backend.
// ---------------------------------------------------------------------------
export const productsService = {
  /** Lista todos os products (admin-only). */
  async getAll(): Promise<Product[]> {
    try {
      const fn = httpsCallable(functions,'listProducts');
      const result = await fn({});
      const response = result.data as ApiResponse<Product[]>;
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch products');
    } catch (error: any) {
      // Propaga mensagem legível do backend (gate: admin/permissão/App Check)
      const message = error?.message || 'Erro ao carregar products';
      console.error('Error fetching products:', error);
      throw new Error(message);
    }
  },

  /** Cria um novo product. */
  async create(data: CreateProductData): Promise<ApiResponse<Product>> {
    const fn = httpsCallable(functions,'createProduct');
    const result = await fn(data);
    return result.data as ApiResponse<Product>;
  },

  /** Atualiza um product existente. */
  async update(id: string, data: UpdateProductData): Promise<ApiResponse> {
    const fn = httpsCallable(functions,'updateProduct');
    const result = await fn({ id, ...data });
    return result.data as ApiResponse;
  },

  /**
   * Remove um product.
   * O backend bloqueia a deleção quando o product é referenciado por algum combo ou list
   * — o erro é retornado como `ApiResponse.error` e deve ser exibido ao usuário.
   */
  async delete(productId: string): Promise<ApiResponse> {
    const fn = httpsCallable(functions,'deleteProduct');
    const result = await fn({ productId });
    return result.data as ApiResponse;
  },
};

// ---------------------------------------------------------------------------
// Serviços para Combos — T101
// ---------------------------------------------------------------------------
export const combosService = {
  /** Lista todos os combos (admin-only). */
  async getAll(): Promise<Combo[]> {
    try {
      const fn = httpsCallable(functions,'listCombos');
      const result = await fn({});
      const response = result.data as ApiResponse<Combo[]>;
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch combos');
    } catch (error: any) {
      const message = error?.message || 'Erro ao carregar combos';
      console.error('Error fetching combos:', error);
      throw new Error(message);
    }
  },

  /** Cria um novo combo. */
  async create(data: CreateComboData): Promise<ApiResponse<Combo>> {
    const fn = httpsCallable(functions,'createCombo');
    const result = await fn(data);
    return result.data as ApiResponse<Combo>;
  },

  /** Atualiza um combo existente. */
  async update(id: string, data: UpdateComboData): Promise<ApiResponse> {
    const fn = httpsCallable(functions,'updateCombo');
    const result = await fn({ id, ...data });
    return result.data as ApiResponse;
  },

  /**
   * Remove um combo.
   * O backend bloqueia a deleção quando há `combo_subscription` viva apontando para ele
   * — o erro é retornado como `ApiResponse.error` e deve ser exibido ao usuário.
   */
  async delete(comboId: string): Promise<ApiResponse> {
    const fn = httpsCallable(functions,'deleteCombo');
    const result = await fn({ comboId });
    return result.data as ApiResponse;
  },
};

// ---------------------------------------------------------------------------
// Serviços para Lists — wrappers atualizados com productId (T101/T112)
// Os callables createList/updateList já existem; agora aceitam productId no payload.
// ---------------------------------------------------------------------------

// Serviços para Users
export const usersService = {
  // Buscar todos os usuários
  async getAll(): Promise<User[]> {
    try {
      const getAllUsersFunction = httpsCallable(functions, 'getAllUsers');
      const result = await getAllUsersFunction({});
      const response = result.data as ApiResponse<User[]>;

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('Failed to fetch users');
    }
  },
};

// Tipos para Entitlements
export interface EntitlementTitleSummary {
  id: string;
  name: string;
  coverUrl: string;
  authors: string[];
  type: 'audiobook' | 'ebook';
  durationSec?: number;
  pages?: number;
}

export interface EntitlementWithDetails {
  id: string;
  userId: string;
  companyId: string;
  titleId: string;
  source: 'csv_upload' | 'payment_webhook' | 'manual';
  grantedAt: any;
  createdAt: any;
  updatedAt: any;
  title: EntitlementTitleSummary;
  company?: {
    id: string;
    name: string;
    cnpj: string;
  };
  titleDetails?: {
    id: string;
    name: string;
    type: 'ebook' | 'audiobook';
    authors: string[];
    publisher: string;
  };
}

export interface ProcessEntitlementUploadResult {
  cpf: string;
  cnpj: string;
  comboId: string;
  comboCodigo: string;
  createdCount: number;
  source: 'csv_upload';
  message: string;
}

// Serviços para Entitlements
export const entitlementsService = {
  // Criar entitlement
  async create(data: any): Promise<ApiResponse> {
    const createEntitlementFunction = httpsCallable(functions, 'createEntitlement');
    const result = await createEntitlementFunction(data);
    return result.data as ApiResponse;
  },

  // Buscar entitlements por usuário
  async getUserEntitlements(userId: string): Promise<EntitlementWithDetails[]> {
    try {
      const getUserEntitlementsFunction = httpsCallable(functions, 'getUserEntitlements');
      const result = await getUserEntitlementsFunction({ userId });
      const response = result.data as ApiResponse<EntitlementWithDetails[]>;

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch user entitlements');
      }
    } catch (error) {
      console.error('Error fetching user entitlements:', error);
      throw new Error('Failed to fetch user entitlements');
    }
  },

  // Processar upload de concessão
  async processEntitlementUpload(cpf: string, cnpj: string, codigoCombo?: string | null): Promise<ApiResponse<ProcessEntitlementUploadResult>> {
    try {
      const processEntitlementUploadFunction = httpsCallable(functions, 'processEntitlementUpload');
      const payload = codigoCombo != null && codigoCombo.trim()
        ? { cpf, cnpj, codigoCombo: codigoCombo.trim() }
        : { cpf, cnpj };
      const result = await processEntitlementUploadFunction(payload);
      return result.data as ApiResponse<ProcessEntitlementUploadResult>;
    } catch (error) {
      console.error('Error processing entitlement upload:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to process entitlement upload');
    }
  },

  // Upload em lote
  async bulkUpload(file: File): Promise<ApiResponse> {
    // TODO: Implementar upload em lote
    // Esta função precisará usar uma URL HTTP direta
    return { success: false, error: 'Not implemented yet' };
  },
};

// Serviços para Sessions
export const sessionsService = {
  // Criar ou atualizar sessão
  async createOrUpdate(data: any): Promise<ApiResponse> {
    const createOrUpdateSessionFunction = httpsCallable(functions, 'createOrUpdateSession');
    const result = await createOrUpdateSessionFunction(data);
    return result.data as ApiResponse;
  },
};

// Tipos para EvoWebhookConfig
export interface EvoWebhookConfig {
  id: string;
  name: string;
  notes?: string;
  dns?: string;
  token?: string;
  /** Token de integração nativa da EVO (REEDY-114). Só `true` é nativo; ausente = token comum. */
  nativeIntegration?: boolean;
  companyId?: string;
  createdAt: any;
  updatedAt: any;
}

export interface CreateEvoWebhookConfigData {
  name: string;
  notes?: string;
}

export interface UpdateEvoWebhookConfigData {
  name?: string;
  notes?: string;
  companyId?: string | null;
}

export interface UpdateEvoWebhookCredentialsData {
  dns: string;
  token: string;
  /** Omitido por admin antigo: o backend mantém a flag salva se o token for o mesmo (P5). */
  nativeIntegration?: boolean;
}

// Serviços para EvoWebhookConfig
export const evoWebhookConfigService = {
  // Criar cadastro
  async create(data: CreateEvoWebhookConfigData): Promise<ApiResponse<EvoWebhookConfig>> {
    const createFunction = httpsCallable(functions, 'createEvoWebhookConfig');
    const result = await createFunction(data);
    return result.data as ApiResponse<EvoWebhookConfig>;
  },

  // Listar todos
  async getAll(): Promise<EvoWebhookConfig[]> {
    try {
      const getAllFunction = httpsCallable(functions, 'getAllEvoWebhookConfigs');
      const result = await getAllFunction({});
      const response = result.data as ApiResponse<EvoWebhookConfig[]>;

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch webhook configs');
      }
    } catch (error) {
      console.error('Error fetching webhook configs:', error);
      throw new Error('Failed to fetch webhook configs');
    }
  },

  // Buscar por ID
  async getById(id: string): Promise<EvoWebhookConfig> {
    try {
      const getByIdFunction = httpsCallable(functions, 'getEvoWebhookConfigById');
      const result = await getByIdFunction({ id });
      const response = result.data as ApiResponse<EvoWebhookConfig>;

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to fetch webhook config');
      }
    } catch (error) {
      console.error('Error fetching webhook config:', error);
      throw new Error('Failed to fetch webhook config');
    }
  },

  // Atualizar cadastro
  async update(id: string, data: UpdateEvoWebhookConfigData): Promise<ApiResponse<EvoWebhookConfig>> {
    const updateFunction = httpsCallable(functions, 'updateEvoWebhookConfig');
    const result = await updateFunction({ id, ...data });
    return result.data as ApiResponse<EvoWebhookConfig>;
  },

  // Atualizar credenciais
  async updateCredentials(id: string, data: UpdateEvoWebhookCredentialsData): Promise<ApiResponse> {
    const updateFunction = httpsCallable(functions, 'updateEvoWebhookCredentials');
    const result = await updateFunction({ id, ...data });
    return result.data as ApiResponse;
  },

  // Deletar cadastro
  async delete(id: string): Promise<ApiResponse> {
    const deleteFunction = httpsCallable(functions, 'deleteEvoWebhookConfig');
    const result = await deleteFunction({ id });
    return result.data as ApiResponse;
  },

  /** Alinha `evoWebhookConfigId` em todas as empresas com base nas configs (admin, idempotente). */
  async backfillCompanyEvoWebhookConfigIds(): Promise<
    ApiResponse<{
      companiesScanned: number;
      webhookConfigsScanned: number;
      configsWithCompanyLink: number;
      companiesUpdated: number;
      companiesCleared: number;
    }>
  > {
    const fn = httpsCallable(functions, 'backfillCompanyEvoWebhookConfigIds');
    const result = await fn({});
    return result.data as ApiResponse<{
      companiesScanned: number;
      webhookConfigsScanned: number;
      configsWithCompanyLink: number;
      companiesUpdated: number;
      companiesCleared: number;
    }>;
  },
};

// Webhooks remotos na API EVO (via Cloud Functions — evita CORS)
export interface EvoRemoteWebhook {
  id: string;
  idBranch?: number;
  eventType?: string;
  urlCallback: string;
  headers?: unknown[];
  filters?: unknown[];
}

export type EvoReedyEventType = 'NewSale' | 'RecurrentSale' | 'ClearedDebt';

/**
 * `data` de `createEvoRemoteWebhooks` em sucesso e nas falhas `partial-create`, `duplicates-detected`
 * e `legacy-duplicates` (`.agents/memory/contracts.md`, REEDY-114). Códigos de falha em `ApiResponse.code`:
 * `evo-list-failed`, `legacy-duplicates`, `partial-create`, `duplicates-detected`.
 */
export interface CreateEvoRemoteWebhooksReport {
  createdCount: number;
  createdEvents: EvoReedyEventType[];
  missingEvents: EvoReedyEventType[];
  duplicateEvents: EvoReedyEventType[];
  compensatedEvents: EvoReedyEventType[];
  /** A releitura da lista na EVO depois da criação funcionou. */
  verified: boolean;
}

export interface EvoConferenceRow {
  saleDate: string;
  idSale: number;
  idMember: number | string;
  memberDocument: string;
  cpfNormalized: string;
  hasReedyItems: boolean;
  hasValidPayment: boolean;
  includedInReport: boolean;
  hasPendingEntitlement: boolean;
  hasPendingReceivable: boolean;
  hasPendingForSaleId: boolean;
  hasAccount: boolean;
  userId: string;
}

export interface EvoConferenceListResult {
  rows: EvoConferenceRow[];
  totalSalesFromEvo: number;
  rowsInReport: number;
  evoListChunks: number;
  companyId: string;
}

export interface EvoConferenceGrantLineResult {
  idSale: string;
  ok: boolean;
  message?: string;
  grantKind?: string;
  saleDateApplied?: boolean;
}

export interface EvoConferenceStampSaleDateLineResult {
  idSale: string;
  status: 'updated' | 'not_found' | 'invalid_sale_date' | 'invalid_cpf' | 'invalid_id_sale';
  pendingDocsUpdated?: number;
}

export interface EvoConferenceStampSaleDatesResult {
  lines: EvoConferenceStampSaleDateLineResult[];
  companyId: string;
  summary: {
    rowsProcessed: number;
    updated: number;
    notFound: number;
    invalidSaleDate: number;
  };
}

export interface AdminShiftAllPendingSaleDatesResult {
  totalScanned: number;
  updated: number;
  skippedNoSaleDate: number;
}

export interface EvoConferenceReconcileCpfResult {
  cpf: string;
  expected: number;
  actualBefore: number;
  actualAfter: number;
  actions: string[];
  warnings: string[];
  ok: boolean;
}

export interface EvoConferenceReconcileSkippedRow {
  idSale: string;
  reason: string;
}

export interface EvoConferenceReconcileResult {
  perCpf: EvoConferenceReconcileCpfResult[];
  skippedRows: EvoConferenceReconcileSkippedRow[];
  companyId: string;
  cpfProcessed: number;
  cpfOk: number;
}

export interface EvoConferenceDuplicatePendingDoc {
  id: string;
  cpf: string;
  titleId: string;
  evoSaleId: number;
  source?: string;
  titleName?: string;
  saleDate?: string;
  processedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  errorMessage?: string;
}

export interface EvoConferenceDuplicatePendingGroup {
  evoSaleId: number;
  docs: EvoConferenceDuplicatePendingDoc[];
}

export interface EvoConferenceDuplicatePendingEvoSaleIdsResult {
  companyId: string;
  totalPendingDocs: number;
  docsWithValidEvoSaleId: number;
  docsWithoutValidEvoSaleId: number;
  duplicateGroupCount: number;
  docsInDuplicateGroups: number;
  duplicateGroups: EvoConferenceDuplicatePendingGroup[];
}

export interface EvoConferenceDeleteDuplicatePendingGroupResult {
  evoSaleId: number;
  keptPendingId: string;
  deletedPendingIds: string[];
}

export interface EvoConferenceDeleteDuplicatePendingEvoSaleIdsResult {
  companyId: string;
  listId: string | null;
  groupsAdjusted: number;
  deletedCount: number;
  perGroup: EvoConferenceDeleteDuplicatePendingGroupResult[];
}

export const evoConferenceService = {
  async listSales(params: { evoWebhookConfigId: string; dateSaleStart: string; dateSaleEnd: string }): Promise<EvoConferenceListResult> {
    const fn = httpsCallable(functions, 'evoConferenceListSales', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<EvoConferenceListResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao listar vendas EVO');
  },

  async grantFromCsv(params: { evoWebhookConfigId: string; csvText: string }): Promise<{
    results: EvoConferenceGrantLineResult[];
    processed: number;
    grantedOrSkippedOk: number;
  }> {
    const fn = httpsCallable(functions, 'evoConferenceGrantFromCsv', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<{
      results: EvoConferenceGrantLineResult[];
      processed: number;
      grantedOrSkippedOk: number;
    }>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao processar CSV');
  },

  async reconcileFromCsv(params: { evoWebhookConfigId: string; csvText: string }): Promise<EvoConferenceReconcileResult> {
    const fn = httpsCallable(functions, 'evoConferenceReconcileFromCsv', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<EvoConferenceReconcileResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha na reconciliação por CPF');
  },

  async stampSaleDatesFromCsv(params: { evoWebhookConfigId: string; csvText: string }): Promise<EvoConferenceStampSaleDatesResult> {
    const fn = httpsCallable(functions, 'evoConferenceStampSaleDatesFromCsv', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<EvoConferenceStampSaleDatesResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao aplicar datas de venda nos pendings');
  },

  async listDuplicatePendingEvoSaleIds(params: { evoWebhookConfigId: string }): Promise<EvoConferenceDuplicatePendingEvoSaleIdsResult> {
    const fn = httpsCallable(functions, 'evoConferenceListDuplicatePendingEvoSaleIds', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<EvoConferenceDuplicatePendingEvoSaleIdsResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao listar duplicados de evoSaleId');
  },

  async deleteDuplicatePendingEvoSaleIds(params: { evoWebhookConfigId: string }): Promise<EvoConferenceDeleteDuplicatePendingEvoSaleIdsResult> {
    const fn = httpsCallable(functions, 'evoConferenceDeleteDuplicatePendingEvoSaleIds', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<EvoConferenceDeleteDuplicatePendingEvoSaleIdsResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao apagar duplicados de evoSaleId');
  },
};

export const pendingEntitlementAdminService = {
  async addThreeHoursToAllSaleDates(): Promise<AdminShiftAllPendingSaleDatesResult> {
    const fn = httpsCallable(functions, 'adminAddThreeHoursToAllPendingEntitlementSaleDates', { timeout: 540_000 });
    const result = await fn({});
    const response = result.data as ApiResponse<AdminShiftAllPendingSaleDatesResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao ajustar saleDate em pending_entitlements');
  },
};

// ---------------------------------------------------------------------------
// Geral Conference
// ---------------------------------------------------------------------------

export interface GeralConferenceRow {
  idSale: number;
  idMember: number | string;
  cpfNormalized: string;
  saleDate: string;
  reedyItemValue: number;
  hasValidPayment: boolean;
  /** Venda já tratada pela conferência: grant/pending_entitlement ou pendingReceivable ainda sem pagamento. */
  alreadyProcessed: boolean;
  /** Livro concedido a usuário cadastrado. */
  hasEntitlement: boolean;
  /** Livro reservado para usuário sem conta — será aplicado no cadastro. */
  hasPendingEntitlement: boolean;
  /** Venda em fila aguardando pagamento. Quando combinado com hasValidPayment, indica venda órfã. */
  hasPendingReceivable: boolean;
  /** Entrega inicial pendente na fila dedicada; bloqueia esta Conferência. */
  hasPendingDelivery: boolean;
  pendingDeliveryItems: number;
  pendingDeliveryQuantity: number;
  hasAccount: boolean;
}

export interface GeralConferenceConfigResult {
  evoWebhookConfigId: string;
  companyId: string;
  companyName: string;
  rows: GeralConferenceRow[];
  error?: string;
}

export interface GeralConferenceListResult {
  byConfig: GeralConferenceConfigResult[];
  totalSalesFromEvo: number;
  totalValid: number;
  totalAlreadyProcessed: number;
}

export interface GeralConferenceExecuteSaleInput {
  evoWebhookConfigId: string;
  idSale: number;
  idMember: number | string;
  cpfNormalized: string;
  saleDate: string;
  reedyItemValue: number;
  hasValidPayment: boolean;
}

export type GeralConferenceGrantKind =
  | 'entitlement_created'
  | 'pending_created'
  | 'pending_receivable_created'
  | 'skipped_pending_delivery'
  | 'skipped_already_processed'
  | 'skipped_already_has_titles'
  | 'skipped_duplicate_title'
  | 'error';

export interface GeralConferenceExecuteLineResult {
  idSale: number;
  cpfNormalized: string;
  evoWebhookConfigId: string;
  ok: boolean;
  grantKind: GeralConferenceGrantKind;
  pendingReceivableCreated: boolean;
  nfCreated: boolean;
  comboSubscriptionCreated: boolean;
  nfError?: string;
  error?: string;
  nfSkipped?: boolean;
  entitlementId?: string;
  pendingEntitlementId?: string;
  pendingReceivableId?: string;
  notaId?: string;
}

export interface GeralConferenceExecuteResult {
  results: GeralConferenceExecuteLineResult[];
  processed: number;
  okCount: number;
  errorCount: number;
}

export const geralConferenceService = {
  async listSalesForConfig(params: {
    dateSaleStart: string;
    dateSaleEnd: string;
    evoWebhookConfigId: string;
  }): Promise<GeralConferenceConfigResult> {
    const fn = httpsCallable(functions, 'geralConferenceListSales', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<GeralConferenceListResult>;
    if (response.success && response.data) {
      return (
        response.data.byConfig[0] ?? {
          evoWebhookConfigId: params.evoWebhookConfigId,
          companyId: '',
          companyName: params.evoWebhookConfigId,
          rows: [],
        }
      );
    }
    throw new Error(response.error || 'Falha ao listar vendas');
  },

  async execute(params: { sales: GeralConferenceExecuteSaleInput[]; createNotas?: boolean }): Promise<GeralConferenceExecuteResult> {
    const fn = httpsCallable(functions, 'geralConferenceExecute', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<GeralConferenceExecuteResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao executar processamento');
  },
};

// ---------------------------------------------------------------------------
// Geral Conference 360
// ---------------------------------------------------------------------------

export interface Geral360Row {
  idSale: number;
  idMember: number | string;
  cpfNormalized: string;
  saleDate: string;
  valorUnitario: number;
}

export interface Geral360ConfigResult {
  evoWebhookConfigId: string;
  companyId: string;
  companyName: string;
  rows: Geral360Row[];
  error?: string;
}

export interface Geral360ListResult {
  byConfig: Geral360ConfigResult[];
  totalFromEvo: number;
  totalMissing: number;
}

export interface Geral360SaleInput {
  evoWebhookConfigId: string;
  idSale: number;
  idMember: number | string;
  cpfNormalized: string;
  saleDate: string;
  valorUnitario: number;
}

export interface Geral360ExecuteLineResult {
  idSale: number;
  cpfNormalized: string;
  evoWebhookConfigId: string;
  ok: boolean;
  subscriptionCreated: boolean;
  nfValueCorrected?: boolean;
  error?: string;
}

export interface Geral360ExecuteResult {
  results: Geral360ExecuteLineResult[];
  processed: number;
  okCount: number;
  errorCount: number;
}

export interface Geral360NotaIncorretaRow {
  subscriptionId: string;
  evoSaleId: number;
  cpf: string;
  companyId: string;
  companyName: string;
  cnpj: string;
  notaId: string;
  valorAtual: number;
  valorCorreto: number;
  fluxoStatus: string | null;
}

export interface Geral360CorrigirNotaInput {
  cnpj: string;
  notaId: string;
  valorCorreto: number;
}

export interface Geral360CorrigirNotaLineResult {
  cnpj: string;
  notaId: string;
  ok: boolean;
  error?: string;
}

export interface Geral360CorrigirNotasResult {
  results: Geral360CorrigirNotaLineResult[];
  okCount: number;
  errorCount: number;
}

export const geral360Service = {
  async listMissingForConfig(params: {
    dateSaleStart: string;
    dateSaleEnd: string;
    evoWebhookConfigId: string;
  }): Promise<Geral360ConfigResult> {
    const fn = httpsCallable(functions, 'geralConference360ListMissing', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<Geral360ListResult>;
    if (response.success && response.data) {
      return (
        response.data.byConfig[0] ?? {
          evoWebhookConfigId: params.evoWebhookConfigId,
          companyId: '',
          companyName: params.evoWebhookConfigId,
          rows: [],
        }
      );
    }
    throw new Error(response.error || 'Falha ao listar subscriptions faltando');
  },

  async execute(params: { sales: Geral360SaleInput[] }): Promise<Geral360ExecuteResult> {
    const fn = httpsCallable(functions, 'geralConference360Execute', { timeout: 540_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<Geral360ExecuteResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao criar subscriptions 360');
  },

  async listNotasIncorretas(): Promise<Geral360NotaIncorretaRow[]> {
    const fn = httpsCallable(functions, 'geralConference360ListNotasIncorretas', { timeout: 540_000 });
    const result = await fn({});
    const response = result.data as ApiResponse<{ rows: Geral360NotaIncorretaRow[]; total: number }>;
    if (response.success && response.data) {
      return response.data.rows;
    }
    throw new Error(response.error || 'Falha ao listar notas incorretas');
  },

  async corrigirNotas(params: { notas: Geral360CorrigirNotaInput[] }): Promise<Geral360CorrigirNotasResult> {
    const fn = httpsCallable(functions, 'geralConference360CorrigirNotas', { timeout: 300_000 });
    const result = await fn(params);
    const response = result.data as ApiResponse<Geral360CorrigirNotasResult>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao corrigir notas');
  },
};

export const evoRemoteWebhooksService = {
  async list(evoWebhookConfigId: string): Promise<EvoRemoteWebhook[]> {
    const listFn = httpsCallable(functions, 'listEvoRemoteWebhooks');
    const result = await listFn({ evoWebhookConfigId });
    const response = result.data as ApiResponse<EvoRemoteWebhook[]>;
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Falha ao listar webhooks na EVO');
  },

  async createAll(evoWebhookConfigId: string): Promise<ApiResponse<CreateEvoRemoteWebhooksReport>> {
    const createFn = httpsCallable(functions, 'createEvoRemoteWebhooks');
    const result = await createFn({ evoWebhookConfigId });
    return result.data as ApiResponse<CreateEvoRemoteWebhooksReport>;
  },

  async deleteAll(evoWebhookConfigId: string): Promise<ApiResponse<{ deleted: number; warnings?: string[] }>> {
    const deleteFn = httpsCallable(functions, 'deleteAllEvoRemoteWebhooks');
    const result = await deleteFn({ evoWebhookConfigId });
    return result.data as ApiResponse<{ deleted: number; warnings?: string[] }>;
  },
};

// ── AdminNotifications (spec 008) ────────────────────────────────────────────

export type AdminNotificationType =
  | 'LIST_NOT_CONFIGURED'
  | 'LIST_MISCONFIGURED'
  | 'LIST_EXHAUSTED';

export interface AdminNotification {
  id: string;
  dedupKey: string;
  type: AdminNotificationType;
  message: string;
  occurrenceCount: number;
  firstOccurrenceAt: string; // ISO string convertido do Timestamp
  lastOccurrenceAt: string;  // ISO string convertido do Timestamp
  resolved: boolean;
}

export const adminNotificationsService = {
  async list(): Promise<AdminNotification[]> {
    const snap = await getDocs(
      query(
        collection(db, 'admin_notifications'),
        where('resolved', '==', false),
        orderBy('lastOccurrenceAt', 'desc')
      )
    );
    return snap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        firstOccurrenceAt: data.firstOccurrenceAt?.toDate?.()?.toISOString() ?? '',
        lastOccurrenceAt: data.lastOccurrenceAt?.toDate?.()?.toISOString() ?? '',
      } as AdminNotification;
    });
  },

  subscribe(
    onData: (notifications: AdminNotification[]) => void,
    onError?: () => void,
  ): () => void {
    const q = query(
      collection(db, 'admin_notifications'),
      where('resolved', '==', false),
      orderBy('lastOccurrenceAt', 'desc')
    );
    return onSnapshot(
      q,
      (snap) => {
        onData(
          snap.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              firstOccurrenceAt: data.firstOccurrenceAt?.toDate?.()?.toISOString() ?? '',
              lastOccurrenceAt: data.lastOccurrenceAt?.toDate?.()?.toISOString() ?? '',
            } as AdminNotification;
          })
        );
      },
      () => onError?.(),
    );
  },

  async resolve(notificationId: string): Promise<void> {
    const fn = httpsCallable(functions, 'resolveAdminNotification');
    await fn({ notificationId });
  },
};

// ── Delivery Queue (spec 009) ─────────────────────────────────────────────────

export type DeliveryQueueFailureReason =
  | 'list_exhausted'
  | 'list_not_configured'
  | 'list_misconfigured';

export interface DeliveryQueueItem {
  id: string;
  cpf: string;
  companyId: string;
  companyName: string | null;
  productId: string;
  productName: string | null;
  productType: 'ebook' | 'audiobook' | null;
  evoSaleId: string;
  comboId: string;
  cycleIndex?: number;
  quantityPending: number;
  quantityDelivered: number;
  failureReason: DeliveryQueueFailureReason;
  status: 'pending' | 'resolved';
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  lastAttemptAt?: string;
}

export type DeliveryQueueFilters = {
  companyId?: string;
  productId?: string;
};

/** Job `processing` sinalizado como sobreposto (filtro parcialmente coincidente) na resposta de `process`. */
export type OverlappingJob = {
  jobId: string;
  filters: DeliveryQueueFilters;
  createdAt: string;
};

export type ProcessDeliveryQueueSyncResult = {
  mode: 'sync';
  resolved: number;
  stillPending: number;
  errors: number;
  totalProcessed: number;
  overlappingJobs?: OverlappingJob[];
};

export type ProcessDeliveryQueueAsyncResult = {
  mode: 'async';
  jobId: string;
  totalItems: number;
  message: string;
  partialResult: {
    resolved: number;
    stillPending: number;
    errors: number;
  };
  overlappingJobs?: OverlappingJob[];
};

export type DeliveryQueueJobStatusData = {
  jobId: string;
  status: 'processing' | 'completed' | 'failed' | 'cancelled';
  totalItems: number;
  processedItems: number;
  resolved: number;
  stillPending: number;
  errors: number;
  createdAt: string;
  completedAt?: string;
  cancelledAt?: string;
};

/** Job `processing` lido direto do Firestore para a seção de jobs em andamento (D4). */
export type DeliveryQueueJobSummary = {
  jobId: string;
  status: 'processing';
  filters: DeliveryQueueFilters;
  totalItems: number;
  processedItems: number;
  resolved: number;
  stillPending: number;
  errors: number;
  createdAt: string;
  updatedAt: string;
  initiatedBy: string;
};

// ---------------------------------------------------------------------------
// Dashboard Analítica (spec 015) — T102
// Região: southamerica-east1 (functionsSa), mesmo padrão de processDeliveryQueue.
// ---------------------------------------------------------------------------

export interface AnalyticsDashboardRequest {
  startDate?: string; // 'YYYY-MM-DD', opcional
  endDate?: string;   // 'YYYY-MM-DD', opcional
}

export interface AnalyticsDashboardData {
  period: { startDate: string; endDate: string; appliedDefault: boolean };
  totalRegisteredUsers: number;
  totalCompanies: number;
  activeUsersInPeriod: number;
  totalTitlesDelivered: number;
  usersWhoReceivedTitles: number;
  donut: { active: number; inactive: number };
  titlesDeliveredHistory: Array<{ period: string; totalTitlesDelivered: number }>;
  usersReceivedTitlesHistory: Array<{ period: string; usersWhoReceivedTitles: number }>;
  funnel: { titlesReceived: number; readingStarted: number; readingCompleted: number };
  deliveriesByCompany: Array<{ companyId: string; companyName: string; totalTitlesDelivered: number }>;
  deliveriesByTitle: Array<{ titleId: string; titleName: string; totalTitlesDelivered: number }>;
  deliveriesByProduct: Array<{ productId: string; productName: string; totalTitlesDelivered: number }>;
}

export const analyticsDashboardService = {
  async getData(request: AnalyticsDashboardRequest): Promise<AnalyticsDashboardData> {
    const fn = httpsCallable(functionsSa, 'analyticsDashboardData');
    const result = await fn(request);
    const payload = result.data as ApiResponse<AnalyticsDashboardData>;
    if (!payload.success || !payload.data) {
      throw new Error(payload.error ?? 'Falha ao carregar dados da dashboard');
    }
    return payload.data;
  },
};

export interface AnalyticsReprocessPeriodRequest {
  startDate: string; // 'YYYY-MM-DD'
  endDate: string;   // 'YYYY-MM-DD'
}

export interface AnalyticsReprocessPeriodResult {
  daysProcessed: number;
  eventsProcessed: number;
  durationMs: number;
}

export const analyticsReprocessPeriodService = {
  async run(request: AnalyticsReprocessPeriodRequest): Promise<AnalyticsReprocessPeriodResult> {
    const fn = httpsCallable(functionsSa, 'analyticsReprocessPeriod', { timeout: 540_000 });
    const result = await fn(request);
    const payload = result.data as ApiResponse<AnalyticsReprocessPeriodResult>;
    if (!payload.success || !payload.data) {
      throw new Error(payload.error ?? 'Falha ao reprocessar período');
    }
    return payload.data;
  },
};

// ── URL base das HTTP Functions na região southamerica-east1 ─────────────────
// Suporta env var explícita REACT_APP_FUNCTIONS_URL_SA (override para staging/dev). Em dev,
// aponta para o emulador (mesmo host:porta de connectFunctionsEmulator em firebase.ts);
// em produção, constrói a partir do project ID do Firebase.
const FUNCTIONS_URL_SA =
  process.env.REACT_APP_FUNCTIONS_URL_SA ??
  (isDev
    ? `http://127.0.0.1:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/southamerica-east1`
    : `https://southamerica-east1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net`);

export const deliveryQueueService = {
  async list(filters: DeliveryQueueFilters = {}): Promise<DeliveryQueueItem[]> {
    const constraints = [
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc'),
    ] as Parameters<typeof query>[1][];

    if (filters.companyId && filters.productId) {
      constraints.splice(
        1,
        0,
        where('companyId', '==', filters.companyId),
        where('productId', '==', filters.productId),
      );
    } else if (filters.companyId) {
      constraints.splice(1, 0, where('companyId', '==', filters.companyId));
    }

    const snap = await getDocs(query(collection(db, 'delivery_queue'), ...constraints));

    let items = snap.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString() ?? '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() ?? '',
        resolvedAt: data.resolvedAt?.toDate?.()?.toISOString(),
        lastAttemptAt: data.lastAttemptAt?.toDate?.()?.toISOString(),
      } as DeliveryQueueItem;
    });

    if (filters.productId && !filters.companyId) {
      items = items.filter((item) => item.productId === filters.productId);
    }

    return items;
  },

  async process(
    filters: DeliveryQueueFilters,
  ): Promise<ProcessDeliveryQueueSyncResult | ProcessDeliveryQueueAsyncResult> {
    const fn = httpsCallable(functionsSa, 'processDeliveryQueue');
    const normalizedFilters: DeliveryQueueFilters = {};
    if (filters.companyId) normalizedFilters.companyId = filters.companyId;
    if (filters.productId) normalizedFilters.productId = filters.productId;
    const result = await fn({ filters: normalizedFilters });
    const payload = result.data as ApiResponse<
      ProcessDeliveryQueueSyncResult | ProcessDeliveryQueueAsyncResult
    >;
    if (!payload.success || !payload.data) {
      throw new Error(payload.error ?? 'Falha ao processar fila');
    }
    return payload.data;
  },

  async getJobStatus(jobId: string): Promise<DeliveryQueueJobStatusData> {
    const fn = httpsCallable(functionsSa, 'getDeliveryQueueJobStatus');
    const result = await fn({ jobId });
    const payload = result.data as ApiResponse<DeliveryQueueJobStatusData>;
    if (!payload.success || !payload.data) {
      throw new Error(payload.error ?? 'Falha ao consultar job');
    }
    return payload.data;
  },

  /**
   * Jobs `processing` em andamento, de qualquer sessão/admin (D4). Leitura direta por SDK,
   * equality-only (`status == 'processing'`, sem `orderBy`) — `delivery_queue_jobs` não tem
   * índice composto para esta query.
   */
  async listProcessingJobs(): Promise<DeliveryQueueJobSummary[]> {
    const snap = await getDocs(
      query(
        collection(db, 'delivery_queue_jobs'),
        where('status', '==', 'processing'),
        limit(50),
      ),
    );

    return snap.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        jobId: docSnap.id,
        status: 'processing',
        filters: data.filters ?? {},
        totalItems: data.totalItems,
        processedItems: data.processedItems,
        resolved: data.resolved,
        stillPending: data.stillPending,
        errors: data.errors,
        createdAt: data.createdAt?.toDate?.()?.toISOString() ?? '',
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() ?? '',
        initiatedBy: data.initiatedBy,
      } as DeliveryQueueJobSummary;
    });
  },

  /** Cancelamento oficial de um job `processing` (D7). Qualquer admin com `entregas_pendentes`/`editar`. */
  async cancelJob(
    jobId: string,
    reason?: string,
  ): Promise<{ jobId: string; status: 'cancelled'; cancelledAt: string; cancelledBy: string }> {
    const fn = httpsCallable(functionsSa, 'cancelDeliveryQueueJob');
    const payload: { jobId: string; reason?: string } = { jobId };
    if (reason && reason.trim()) payload.reason = reason.trim();
    const result = await fn(payload);
    const response = result.data as ApiResponse<{
      jobId: string;
      status: 'cancelled';
      cancelledAt: string;
      cancelledBy: string;
    }>;
    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Falha ao cancelar job');
    }
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Relatórios Administrativos de Entregas (REEDY-16 / spec 036)
// Região: southamerica-east1 (functionsSa), mesmo padrão de analyticsDashboardData.
// Contrato completo: .claude/memorys/contracts.md seção "Relatórios Administrativos de Entregas".
// ---------------------------------------------------------------------------

export interface GetDeliveriesReportRequest {
  dimension: 'company' | 'publisher' | 'combo';
  dateStart: string;  // 'YYYY-MM-DD'
  dateEnd: string;    // 'YYYY-MM-DD'
  filters?: {
    companyId?: string;
    publisherId?: string;
    productId?: string;
    comboId?: string;
  };
  page?: number;      // 1-based, default 1
  pageSize?: number;  // default 50, max 200
}

export interface DeliveriesReportProductBreakdown {
  productId: string;
  productName: string;
  quantity: number;
}

export interface DeliveriesReportProductColumn {
  productId: string;
  productName: string;
}

export interface DeliveriesReportRow {
  dimensionId: string;
  dimensionName: string;
  /** Identificador da coluna de detalhamento — significado depende da dimensão. Sempre null para 'combo'. */
  secondaryId: string | null;
  /** Nome de exibição da coluna de detalhamento. Sempre null para 'combo'. */
  secondaryName: string | null;
  quantity: number;
  /** Somente para dimension:'combo' — pivot por produto (decisão do PO, 2026-08-07). */
  productBreakdown?: DeliveriesReportProductBreakdown[];
}

export interface DeliveriesReportPagination {
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
}

export interface GetDeliveriesReportResponse {
  success: true;
  data: {
    rows: DeliveriesReportRow[];
    pagination: DeliveriesReportPagination;
    summary: {
      totalDeliveries: number;
    };
    /** Somente para dimension:'combo' — colunas de produto globais (todas as páginas). */
    productColumns?: DeliveriesReportProductColumn[];
  };
}

export const deliveriesReportService = {
  /**
   * Callable paginada — retorna uma página de rows + metadados de paginação.
   * Timeout de 300s: o backend recomputa a agregação a cada chamada (3-10s para períodos curtos).
   */
  async getDeliveriesReport(
    params: GetDeliveriesReportRequest
  ): Promise<GetDeliveriesReportResponse> {
    const fn = httpsCallable(functionsSa, 'getDeliveriesReport', { timeout: 300_000 });
    const result = await fn(params);
    return result.data as GetDeliveriesReportResponse;
  },

  /**
   * Export CSV — fetch direto para HTTP function com Bearer token, retorna Blob.
   * NÃO é httpsCallable. O backend gera o CSV com TODAS as linhas (sem paginação).
   *
   * REEDY-23: por ser `fetch` puro, o SDK não injeta `X-Firebase-AppCheck` aqui — o header é
   * montado na mão, ao lado do `Authorization: Bearer` (contrato: App Check é aditivo, não
   * substitui a autorização). Falha ao obter o token é registrada nomeando App Check e a
   * requisição segue: o enforcement é do backend, e um erro local não pode derrubar o download
   * antes de a Fase 2 estar no ar.
   */
  async downloadDeliveriesReportCsv(
    params: Omit<GetDeliveriesReportRequest, 'page' | 'pageSize'>,
    idToken: string
  ): Promise<Blob> {
    const exportUrl = `${FUNCTIONS_URL_SA}/exportDeliveriesReportCsv`;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
    if (appCheck) {
      try {
        const { token } = await getAppCheckToken(appCheck);
        headers['X-Firebase-AppCheck'] = token;
      } catch (error) {
        console.error(
          '[App Check] Falha ao obter token de App Check para exportDeliveriesReportCsv; ' +
            'a requisição seguirá sem o header X-Firebase-AppCheck e o backend pode respondê-la ' +
            'com 401 (unauthenticated).',
          error
        );
      }
    }
    const response = await fetch(exportUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const errorBody = await response
        .json()
        .catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(errorBody.error || `HTTP ${response.status}`);
    }
    return response.blob();
  },
};

// ── Controle de Acesso Administrativo (RBAC) — REEDY-17 ─────────────────────
// Vocabulário e shapes fixados por `.agents/memory/contracts.md`
// §"Controle de Acesso Administrativo (RBAC) — requisito transversal de autorização".
// Estado do contrato no momento desta implementação: REGISTRADO, NÃO IMPLANTADO — o backend
// real (`reedy-functions/src/functions/adminAccess/**`) é entregue em paralelo por outro
// Developer; este arquivo programa contra o contrato, não contra código lido.

/** 16 módulos = 15 itens de menu (`components/Layout.tsx`) + o módulo novo "Administradores". */
export type AdminModuleId =
  | 'analytics'
  | 'empresas'
  | 'redes'
  | 'titulos'
  | 'editoras'
  | 'listas'
  | 'ativos'
  | 'combos'
  | 'upload'
  | 'usuarios'
  | 'webhooks_evo'
  | 'conferencia_evo'
  | 'notificacoes'
  | 'entregas_pendentes'
  | 'relatorios'
  | 'administradores';

/** Nível ORDENADO, não dois booleanos: 'editar' ⊃ 'ver'. Uma exigência de 'ver' é satisfeita por 'ver' OU 'editar'. */
export type AdminAction = 'ver' | 'editar';

export type AdminPermissions = Partial<Record<AdminModuleId, AdminAction>>;

export const ADMIN_MODULE_LABELS: Record<AdminModuleId, string> = {
  analytics: 'Analytics',
  empresas: 'Empresas',
  redes: 'Redes',
  titulos: 'Títulos',
  editoras: 'Editoras',
  listas: 'Listas',
  ativos: 'Ativos',
  combos: 'Combos',
  upload: 'Upload',
  usuarios: 'Usuários',
  webhooks_evo: 'Webhooks EVO',
  conferencia_evo: 'Conferência EVO',
  notificacoes: 'Notificações',
  entregas_pendentes: 'Entregas Pendentes',
  relatorios: 'Relatórios',
  administradores: 'Administradores',
};

/** Ordem estável de exibição da matriz de permissões (mesma ordem do contrato/menu). */
export const ADMIN_MODULE_ORDER: AdminModuleId[] = [
  'analytics',
  'empresas',
  'redes',
  'titulos',
  'editoras',
  'listas',
  'ativos',
  'combos',
  'upload',
  'usuarios',
  'webhooks_evo',
  'conferencia_evo',
  'notificacoes',
  'entregas_pendentes',
  'relatorios',
  'administradores',
];

/** Resposta de `getMyAdminAccess` — o que o próprio administrador logado enxerga de si. */
export interface MyAdminAccess {
  uid: string;
  email: string;
  status: 'active' | 'disabled';
  permissions: AdminPermissions;
}

/** Resposta de `listAdminUsers` (item) — nunca inclui `admin_users` cru (coleção fechada ao SDK cliente). */
export interface AdminUserSummary {
  uid: string;
  email: string;
  displayName?: string;
  status: 'active' | 'disabled';
  permissions: AdminPermissions;
  authDisabled: boolean;
  claimInSync: boolean;
  lastSignInAt?: string;
  createdAt: string;
  updatedAt: string;
  updatedByEmail?: string;
}

export interface CreateAdminUserInput {
  email: string;
  password?: string;
  displayName?: string;
  permissions: AdminPermissions;
  /** Segundo passo do fluxo de e-mail já existente — ver `extractReuseUidConflict`. */
  confirmReuseUid?: string;
}

export interface CreateAdminUserResult {
  admin: AdminUserSummary;
  createdAuthUser: boolean;
  reusedAuthAccount: boolean;
}

export interface UpdateAdminUserInput {
  uid: string;
  permissions: AdminPermissions;
  displayName?: string;
}

export interface SetAdminUserStatusInput {
  uid: string;
  status: 'active' | 'disabled';
}

export interface DeleteAdminUserInput {
  uid: string;
}

/**
 * Evidência controlada devolvida no passo 1 de `createAdminUser` quando o e-mail já tem conta
 * Auth (`contracts.md` §"createAdminUser — fluxo de dois passos"). O contrato não fixa valor
 * literal de `reason` para este ramo — por isso a detecção em `extractReuseUidConflict` é
 * estrutural (forma do payload), não por `reason`.
 */
export interface CreateAdminUserConflict {
  uid: string;
  creationTime?: string;
  lastSignInTime?: string;
  emailVerified?: boolean;
  providerIds?: string[];
  hasAppUserDoc?: boolean;
  hasFiscalUserDoc?: boolean;
}

export interface AdminAccessError extends Error {
  /** Código já sem o prefixo `functions/` do SDK (`permission-denied`, `failed-precondition`, ...). */
  code: string;
  details?: Record<string, unknown>;
}

function stripFunctionsPrefix(code: unknown): string {
  return typeof code === 'string' ? code.replace(/^functions\//, '') : 'unknown';
}

function toAdminAccessError(error: unknown): AdminAccessError {
  const raw = error as { code?: unknown; message?: unknown; details?: unknown };
  const message = typeof raw?.message === 'string' && raw.message
    ? raw.message
    : 'Não foi possível concluir a operação.';
  const normalized = new Error(message) as AdminAccessError;
  normalized.code = stripFunctionsPrefix(raw?.code);
  normalized.details = raw?.details && typeof raw.details === 'object'
    ? (raw.details as Record<string, unknown>)
    : undefined;
  return normalized;
}

/**
 * Detecta o ramo (c) do fluxo de dois passos de `createAdminUser` — e-mail já existente,
 * aguardando `confirmReuseUid`. Distinção **estrutural** (presença de `uid` na evidência de conta
 * Auth), não por `reason` literal — ver comentário em `CreateAdminUserConflict`. O `uid` é o
 * discriminante real contra os demais gatilhos de `failed-precondition` do domínio (autoação e
 * cardinalidade, `adminAccess/errors.ts`), que nunca carregam `uid` em `details`.
 */
export function extractReuseUidConflict(error: AdminAccessError): CreateAdminUserConflict | null {
  if (error.code !== 'failed-precondition' || !error.details) return null;
  if (typeof error.details.uid !== 'string') return null;
  return error.details as unknown as CreateAdminUserConflict;
}

/**
 * O SDK do `httpsCallable` serializa `undefined` como `null` (não omite a chave, como
 * `JSON.stringify` faria) — um campo opcional não preenchido (`displayName`,
 * `confirmReuseUid`) chega ao backend como `null`, e os schemas Joi das
 * callables de `adminAccess` usam `.optional()` (chave ausente), não `.allow(null)`,
 * rejeitando com `"<campo>" must be a boolean/string`. Remover as chaves `undefined` do
 * payload antes de enviar evita essa classe inteira de falso "campo obrigatório".
 */
function stripUndefined(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return payload;
  return Object.fromEntries(
    Object.entries(payload as Record<string, unknown>).filter(([, value]) => value !== undefined)
  );
}

async function callAdminAccess<T>(name: string, payload: unknown): Promise<T> {
  let result;
  try {
    result = await httpsCallable(functionsSa, name)(stripUndefined(payload));
  } catch (error) {
    throw toAdminAccessError(error);
  }
  const response = result.data as ApiResponse<T>;
  if (!response?.success || response.data === undefined) {
    throw toAdminAccessError({ code: 'internal', message: response?.error });
  }
  return response.data;
}

export const adminAccessService = {
  getMyAdminAccess: () => callAdminAccess<MyAdminAccess>('getMyAdminAccess', {}),
  listAdminUsers: () => callAdminAccess<{ items: AdminUserSummary[] }>('listAdminUsers', {})
    .then((result) => result.items),
  createAdminUser: (input: CreateAdminUserInput) => callAdminAccess<CreateAdminUserResult>(
    'createAdminUser',
    input,
  ),
  updateAdminUser: (input: UpdateAdminUserInput) => callAdminAccess<{ admin: AdminUserSummary }>(
    'updateAdminUser',
    input,
  ).then((result) => result.admin),
  setAdminUserStatus: (input: SetAdminUserStatusInput) => callAdminAccess<{ admin: AdminUserSummary }>(
    'setAdminUserStatus',
    input,
  ).then((result) => result.admin),
  deleteAdminUser: (input: DeleteAdminUserInput) => callAdminAccess<{ uid: string }>(
    'deleteAdminUser',
    input,
  ),
};
